import { useCallback, useEffect, useState } from 'react';
import api from '../../API';
import PrincipalAccessEditor, { ACCESS_LEVELS } from '../../components/PrincipalAccessEditor';
import styles from './CreateFolderModal.module.css';

/**
 * Resolve user or group id to display name via Girder API.
 */
async function resolvePrincipal(type, id) {
    const path = type === 'user' ? 'user' : 'group';
    try {
        const r = await api.get(`${path}/${id}`, {});
        if (r.status === 200 && r.data) {
            return type === 'user' ? (r.data.login || r.data.name || id) : (r.data.name || id);
        }
    } catch (_) {
        // ignore
    }
    return id;
}

/**
 * Modal to view and edit folder access (users and groups with read/write/admin).
 * When allowPublicToggle is true (Slide Sets), Public Yes/No is editable and sent on save.
 */
function FolderAccessModal({
    isOpen,
    onClose,
    folderId,
    folderName,
    onSaved,
    allowPublicToggle = false,
    folderType = null,
}) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [displayNames, setDisplayNames] = useState({ users: {}, groups: {} });

    const enablePublic = allowPublicToggle || folderType === 'slideset';

    const load = useCallback(() => {
        if (!folderId || !isOpen) return;
        setLoading(true);
        setError(null);
        api.get(`projects/folders/${folderId}/access`, {})
            .then((r) => {
                if (r.status === 200) {
                    setData(r.data);
                } else {
                    setError(r.data?.message || 'Failed to load access.');
                }
            })
            .catch((e) => setError(e.message || 'Failed to load access.'))
            .finally(() => setLoading(false));
    }, [folderId, isOpen]);

    useEffect(() => {
        load();
    }, [load]);

    useEffect(() => {
        if (!data?.access) {
            setDisplayNames({ users: {}, groups: {} });
            return;
        }
        const users = data.access.users || [];
        const groups = data.access.groups || [];
        const userPromises = users.map((u) => resolvePrincipal('user', u.id));
        const groupPromises = groups.map((g) => resolvePrincipal('group', g.id));
        Promise.all([...userPromises, ...groupPromises]).then((names) => {
            const n = users.length;
            const usersMap = {};
            users.forEach((u, i) => { usersMap[u.id] = names[i]; });
            const groupsMap = {};
            groups.forEach((g, i) => { groupsMap[g.id] = names[n + i]; });
            setDisplayNames({ users: usersMap, groups: groupsMap });
        });
    }, [data?.access]);

    function handleAccessChange(access) {
        if (!data) return;
        setData({ ...data, access });
    }

    function handlePublicChange(nextPublic) {
        if (!data) return;
        setData({ ...data, public: nextPublic });
    }

    function handleSave() {
        if (!data || data.myAccess < 2) return;
        setSaving(true);
        setError(null);
        const body = { access: data.access };
        if (enablePublic) {
            body.public = Boolean(data.public);
        }
        api.put(`projects/folders/${folderId}/access`, { json: body })
            .then((r) => {
                if (r.status === 200) {
                    onSaved?.();
                    onClose();
                } else {
                    setError(r.data?.message || 'Failed to save.');
                }
            })
            .catch((e) => setError(e.message || 'Failed to save.'))
            .finally(() => setSaving(false));
    }

    if (!isOpen) return null;

    return (
        <div className={styles.modalOverlay} onClick={onClose} role="presentation">
            <div
                className={`${styles.modalContent} ${styles.accessModalContent}`}
                onClick={(e) => e.stopPropagation()}
                role="dialog"
                aria-labelledby="folder-access-title"
            >
                <div className={styles.modalHeader}>
                    <h2 id="folder-access-title" className={styles.modalTitle}>
                        Sharing: {folderName || folderId}
                    </h2>
                    <button type="button" className={styles.closeButton} onClick={onClose} aria-label="Close">
                        ×
                    </button>
                </div>
                <div className={`${styles.modalBody} ${styles.accessModalBody}`}>
                    {loading && <p>Loading…</p>}
                    {error && <div className={styles.errorMessage}>{error}</div>}
                    {data && !loading && (
                        <>
                            <div className={styles.fieldGroup}>
                                <span className={styles.fieldLabel}>Public access</span>
                                {enablePublic && data.myAccess >= 2 ? (
                                    <div className={styles.radioGroup} role="radiogroup" aria-label="Public access">
                                        <label className={styles.radioLabel}>
                                            <input
                                                type="radio"
                                                name="folder-public"
                                                checked={!data.public}
                                                onChange={() => handlePublicChange(false)}
                                            />
                                            <span>
                                                <strong>Private</strong>
                                                <span className={styles.radioDescription}>
                                                    Only users and groups listed below can view.
                                                </span>
                                            </span>
                                        </label>
                                        <label className={styles.radioLabel}>
                                            <input
                                                type="radio"
                                                name="folder-public"
                                                checked={Boolean(data.public)}
                                                onChange={() => handlePublicChange(true)}
                                            />
                                            <span>
                                                <strong>Public</strong>
                                                <span className={styles.radioDescription}>
                                                    Anyone with the link can view (including logged-out users).
                                                </span>
                                            </span>
                                        </label>
                                    </div>
                                ) : (
                                    <span className={styles.publicReadonly}>
                                        {data.public ? 'Yes (public)' : 'No (private)'}
                                    </span>
                                )}
                            </div>
                            <div className={styles.accessDivider} />
                            <PrincipalAccessEditor
                                value={data.access || { users: [], groups: [] }}
                                onChange={handleAccessChange}
                                displayNames={displayNames}
                                levels={ACCESS_LEVELS}
                                disabled={data.myAccess < 2}
                            />
                        </>
                    )}
                </div>
                {data && data.myAccess >= 2 && (
                    <div className={styles.modalFooter}>
                        <button type="button" className={styles.cancelButton} onClick={onClose}>
                            Cancel
                        </button>
                        <button
                            type="button"
                            className={styles.submitButton}
                            onClick={handleSave}
                            disabled={saving}
                        >
                            {saving ? 'Saving…' : 'Save'}
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}

export default FolderAccessModal;
